import {
  Box,
  Flex,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  Image,
  useDisclosure,
  InputGroup,
  InputRightElement,
  Badge,
  Progress,
  SimpleGrid,
  Skeleton,
} from "@chakra-ui/react";
import React, { memo, useState, useMemo } from "react";
import IconX from "../../../assets/images/booking/x-circle.svg";
import IconCheck from "../../../assets/images/booking/check-circle.svg";
import IconRed from "../../../assets/images/booking/x-red.svg";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import EmployeeAttendanceMonthTable from "../employeeAttendanceMonthTable";
import DatePicker from "react-datepicker";
import { useEmployeeAttendanceMonth } from "../../../hooks/useEmployeeAttendanceMonthly";

// ---- helpers ----
const toMonthParam = (d) => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${yyyy}`; // backend expect "MM-YYYY"
};

const fmtInt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("vi-VN") : "0";

const fmt1 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(1) : "0.0");

const fmtPct0 = (n) =>
  Number.isFinite(Number(n)) ? `${Math.round(Number(n))}%` : "0%";

const fmtPct2 = (n) =>
  Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "0.00%";

const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const StatCard = ({ label, value, sub, color = "#51607B" }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="12px"
    p="12px"
    minH="72px"
  >
    <Text fontSize="12px" color="#718096" fontWeight={600}>
      {label}
    </Text>
    <Text fontSize="18px" fontWeight={800} color="var(--color-info-employee)">
      {value}
    </Text>
    {sub ? (
      <Text fontSize="12px" color={color} mt="2px">
        {sub}
      </Text>
    ) : null}
  </Box>
);

const FormInfo = memo(({ isOpen, onClose, defaultValues }) => {
  const [zIndex, setZIndex] = useState(1);
  const [monthDate, setMonthDate] = useState(null);

  const chartParams = useMemo(() => {
    const p = {};
    if (defaultValues?.employeeId) p.employeeId = defaultValues.employeeId;
    if (monthDate) {
      p.month = toMonthParam(monthDate);
      p.year = monthDate.getFullYear(); // nếu hook/backend cần
    }
    return p;
  }, [defaultValues, monthDate]);

  const {
    employeeAttendanceMonthData,
    isLoading,
    isRefetching,
    employee,
    range,
    summary,
  } = useEmployeeAttendanceMonth({ ...chartParams });

  // -------- computed attendance UI --------
  const totalExpected = safeNum(summary?.totalExpected, 0);
  const totalCredit = safeNum(summary?.totalCredit, 0);

  const attendanceRate = useMemo(() => {
    if (!totalExpected) return 0;
    return totalCredit / totalExpected;
  }, [totalCredit, totalExpected]);

  const attendancePct = safeNum(
    summary?.attendanceRatePct,
    attendanceRate * 100
  );

  const isWarning = summary?.warning ?? attendanceRate < 0.7;

  const badgeBg = isWarning ? "red.50" : "green.50";
  const badgeColor = isWarning ? "red.600" : "green.600";
  const badgeText = isWarning ? "Không đạt (< 70%)" : "Đạt (≥ 70%)";
  const badgeIcon = isWarning ? IconRed : IconCheck;

  const counts = summary?.counts || {};

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        position={"relative"}
        w={"100%"}
        maxW={"70%"}
      >
        <Box
          paddingBottom={"80px"}
          h={"86.5vh"}
          w={"100%"}
          overflowY={"auto"}
          sx={{
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              width: "4px",
              borderRadius: "50px",
              background: "var(--color-background)",
            },
          }}
        >
          {/* Header */}
          <Flex
            alignItems={"center"}
            p={"25px 24px 16px 24px"}
            position={"relative"}
            borderBottom={"1px solid var(--border-input-employee)"}
          >
            <Box>
              <Text
                color={"var(--color-header-employee)"}
                fontSize={"16px"}
                fontWeight={"700"}
              >
                Chi tiết chuyên cần
              </Text>

              {/* Employee + Range */}
              <Text fontSize="12px" color="#51607B" mt="4px">
                {employee?.fullName || defaultValues?.nameEmployee || "-"}{" "}
                {employee?.code ? `(${employee.code})` : ""}
                {range?.start || range?.end ? (
                  <>
                    {" "}
                    • {range?.start || "--/--/----"} -{" "}
                    {range?.end || "--/--/----"}
                  </>
                ) : null}
              </Text>
            </Box>

            <Box
              position={"absolute"}
              top={"18px"}
              right={"18px"}
              cursor={"pointer"}
              onClick={onClose}
            >
              <Image src={IconX} />
            </Box>
          </Flex>

          {/* Bộ lọc */}
          <Flex
            alignItems={"center"}
            gap={{ base: "16px 32px" }}
            flex={"1"}
            flexWrap={"wrap"}
            w={"100%"}
            justifyContent={"center"}
            pt="16px"
          >
            <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
              <DatePicker
                selected={monthDate}
                onChange={(d) => setMonthDate(d)}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                locale="vi"
                isClearable
                placeholderText="Chọn tháng"
                className="datePickerStyle"
                onFocus={() => setZIndex(3)}
                onBlur={() => setZIndex(1)}
              />
              {!monthDate && (
                <InputRightElement pointerEvents="none" h={"44px"}>
                  <Image src={calendarIcon} />
                </InputRightElement>
              )}
            </InputGroup>
          </Flex>

          {/* Tổng kết chuyên cần (NEW) */}
          <Box px="24px" pt="18px">
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              <Box
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="14px"
                p="16px"
              >
                <Flex
                  align="flex-start"
                  justify="space-between"
                  gap="12px"
                  flexWrap="wrap"
                >
                  <Box>
                    <Text
                      fontSize="14px"
                      fontWeight={800}
                      color="var(--color-info-employee)"
                    >
                      Điểm chuyên cần
                    </Text>
                    <Flex align="center" gap="10px" mt="6px" flexWrap="wrap">
                      <Badge
                        bg={badgeBg}
                        color={badgeColor}
                        border="1px solid"
                        borderColor={isWarning ? "red.200" : "green.200"}
                        borderRadius="999px"
                        px="10px"
                        py="4px"
                        fontSize="12px"
                        fontWeight={800}
                      >
                        <Flex align="center" gap="6px">
                          <Image src={badgeIcon} w="14px" h="14px" />
                          <Text>{badgeText}</Text>
                        </Flex>
                      </Badge>

                      <Text fontSize="12px" color="#51607B">
                        Công:{" "}
                        <Text as="span" fontWeight={800} color="#1A202C">
                          {fmt1(totalCredit)}
                        </Text>
                        {" / "}
                        <Text as="span" fontWeight={800} color="#1A202C">
                          {fmt1(totalExpected)}
                        </Text>
                      </Text>
                    </Flex>
                  </Box>

                  <Box minW="220px">
                    <Text fontSize="12px" color="#718096" fontWeight={700}>
                      Tỉ lệ
                    </Text>
                    <Text
                      fontSize="28px"
                      fontWeight={700}
                      color={isWarning ? "red.600" : "green.600"}
                      lineHeight="1.1"
                    >
                      {fmtPct2(attendancePct)}
                    </Text>
                  </Box>
                </Flex>

                {/* Progress */}
                <Box mt="12px">
                  <Progress
                    value={Math.min(100, Math.max(0, attendancePct))}
                    size="sm"
                    borderRadius="999px"
                    colorScheme={isWarning ? "red" : "green"}
                  />
                  <Text fontSize="11px" color="#718096" mt="6px">
                    Quy tắc: điểm chuyên cần = tổng công / tổng công chuẩn của
                    tháng. Nếu &lt; 70% sẽ cảnh báo đỏ.
                  </Text>
                </Box>

                {/* Stats grid */}
                <SimpleGrid
                  columns={{ base: 2, md: 3, lg: 6 }}
                  spacing="10px"
                  mt="14px"
                >
                  <StatCard
                    label="Đi làm"
                    value={fmtInt(counts?.checkinDays || 0)}
                  />
                  <StatCard
                    label="Chấm công ngoài"
                    value={fmtInt(counts?.registerOutsideDays || 0)}
                  />
                  <StatCard
                    label="Làm bù"
                    value={fmtInt(counts?.makeupWorkDays || 0)}
                  />
                  <StatCard
                    label="Nghỉ phép"
                    value={fmtInt(counts?.leaveDays || 0)}
                  />
                  <StatCard
                    label="Vắng"
                    value={fmtInt(counts?.absentDays || 0)}
                    sub={isWarning ? "Cần theo dõi" : undefined}
                    color={isWarning ? "red.600" : "#51607B"}
                  />
                  <StatCard
                    label="Ngày nghỉ"
                    value={fmtInt(counts?.holidayDays || 0)}
                  />
                </SimpleGrid>
              </Box>
            </Skeleton>
          </Box>

          {/* Danh sách */}
          <Box p={"24px"}>
            <EmployeeAttendanceMonthTable
              employeeAttendanceMonthData={employeeAttendanceMonthData}
              isLoading={isLoading}
              isRefetching={isRefetching}
            />
          </Box>

          <Box
            position={"absolute"}
            w={"100%"}
            bottom={"0"}
            bg={"var(--color-main)"}
            borderTop={"0.5px solid var(--bg-line-employee)"}
            borderRadius={"0px 0px 16px 16px"}
            h="12px"
          />
        </Box>
      </ModalContent>
    </Modal>
  );
});

export default FormInfo;
